import React, { useState } from 'react';
import { X, Send, MessageCircle, Loader } from 'lucide-react';
import api from '../services/api';

const LeaveMessageModal = ({ isOpen, onClose, initialMessage = '', context = '' }) => {
  const [message, setMessage] = useState(initialMessage);
  const [priority, setPriority] = useState('normal');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!message.trim()) {
      setError('请输入留言内容');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await api.post('/api/ai-assistant/leave-message', {
        message: message.trim(),
        context,
        priority
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.error || '提交失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setMessage('');
    setPriority('normal');
    setSubmitted(false);
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-teal-500 to-cyan-500 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <MessageCircle className="w-5 h-5" />
            <h3 className="font-semibold">留言给咨询师</h3>
          </div>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-teal-600" />
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2">
                留言已提交
              </h4>
              <p className="text-gray-600 mb-6">
                咨询师会尽快查看您的留言并与您联系，请留意站内消息或短信通知。
              </p>
              <button
                onClick={handleClose}
                className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors"
              >
                知道了
              </button>
            </div>
          ) : (
            <>
              <p className="text-gray-600 mb-4">
                请描述您的问题或困扰，咨询师会尽快与您联系。
              </p>

              {/* Priority Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  紧急程度
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPriority('low')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      priority === 'low'
                        ? 'bg-green-100 text-green-700 border-2 border-green-300'
                        : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    一般咨询
                  </button>
                  <button
                    onClick={() => setPriority('normal')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      priority === 'normal'
                        ? 'bg-yellow-100 text-yellow-700 border-2 border-yellow-300'
                        : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    需要帮助
                  </button>
                  <button
                    onClick={() => setPriority('high')}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      priority === 'high'
                        ? 'bg-red-100 text-red-700 border-2 border-red-300'
                        : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                    }`}
                  >
                    比较紧急
                  </button>
                </div>
              </div>

              {/* Message Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  留言内容
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="请详细描述您的问题，包括：\n• 您的困扰是什么\n• 持续了多长时间\n• 希望得到什么样的帮助"
                  className="w-full h-32 px-4 py-3 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-lg font-medium hover:from-teal-600 hover:to-cyan-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    提交中...
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    提交留言
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-gray-400 text-center">
                您的信息将被严格保密，仅咨询师可见
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeaveMessageModal;
